// @ts-nocheck
/**
 * Professional Profile Page - Carbon Design System Compliant
 * Fully implements Carbon components for individual professional learning
 * WCAG 2.1 AA accessibility compliant
 */

import React, { useState, useEffect } from 'react';
import {
  Content,
  Grid,
  Column,
  Heading,
  Stack,
  Button,
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tag,
  ProgressBar,
  Card,
  Tile,
  Modal,
  Form,
  FormGroup,
  TextInput,
  TextArea,
  Select,
  SelectItem,
  InlineNotification,
  Loading,
  Breadcrumb,
  BreadcrumbItem,
} from '@carbon/react';
import { User, Certificate, Growth, Analytics, Book, Trophy, Portfolio, Edit, Add, View } from '@carbon/icons-react';

interface Skill {
  id: string;
  name: string;
  category: string;
  level: 'Beginner' | 'Intermediate' | 'Advanced' | 'Expert';
  progress: number;
  lastUpdated: string;
}

interface Certification {
  id: string;
  name: string;
  issuer: string;
  dateEarned: string;
  expiryDate: string;
  status: 'Active' | 'Expired' | 'In Progress';
  credentialId: string;
}

interface LearningGoal {
  id: string;
  title: string;
  description: string;
  targetDate: string;
  progress: number;
  status: 'Not Started' | 'In Progress' | 'Completed';
}

const ProfessionalProfile: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sample data - in real app, this would come from API
  const [professionalInfo, setProfessionalInfo] = useState({
    fullName: 'Jane Professional',
    title: 'Software Engineer',
    company: 'Tech Corp',
    industry: 'Technology',
    yearsExperience: 5,
    location: 'San Francisco, CA',
    bio: 'Passionate software engineer focused on continuous learning and professional development.',
    linkedIn: 'https://linkedin.com/in/janeprofessional',
    portfolio: 'https://janeportfolio.com',
  });

  const [skills, setSkills] = useState<Skill[]>([
    {
      id: '1',
      name: 'React Development',
      category: 'Frontend',
      level: 'Advanced',
      progress: 85,
      lastUpdated: '2024-01-15',
    },
    {
      id: '2',
      name: 'Node.js',
      category: 'Backend',
      level: 'Intermediate',
      progress: 65,
      lastUpdated: '2024-01-10',
    },
    {
      id: '3',
      name: 'Cloud Architecture',
      category: 'Infrastructure',
      level: 'Beginner',
      progress: 35,
      lastUpdated: '2024-01-08',
    },
  ]);

  const [certifications, setCertifications] = useState<Certification[]>([
    {
      id: '1',
      name: 'AWS Certified Developer',
      issuer: 'Amazon Web Services',
      dateEarned: '2023-06-15',
      expiryDate: '2026-06-15',
      status: 'Active',
      credentialId: 'AWS-DEV-123456',
    },
    {
      id: '2',
      name: 'React Professional Certificate',
      issuer: 'Meta',
      dateEarned: '2023-03-22',
      expiryDate: '2025-03-22',
      status: 'Active',
      credentialId: 'META-REACT-789012',
    },
  ]);

  const [learningGoals, setLearningGoals] = useState<LearningGoal[]>([
    {
      id: '1',
      title: 'Master Kubernetes',
      description: 'Learn container orchestration and deployment strategies',
      targetDate: '2024-06-30',
      progress: 45,
      status: 'In Progress',
    },
    {
      id: '2',
      title: 'Complete Machine Learning Course',
      description: 'Understand ML fundamentals and practical applications',
      targetDate: '2024-09-15',
      progress: 20,
      status: 'In Progress',
    },
  ]);

  useEffect(() => {
    // Simulate API loading
    setTimeout(() => setLoading(false), 1000);
  }, []);

  const skillTableHeaders = [
    { key: 'name', header: 'Skill' },
    { key: 'category', header: 'Category' },
    { key: 'level', header: 'Level' },
    { key: 'progress', header: 'Progress' },
    { key: 'lastUpdated', header: 'Last Updated' },
  ];

  const certificationTableHeaders = [
    { key: 'name', header: 'Certification' },
    { key: 'issuer', header: 'Issuer' },
    { key: 'dateEarned', header: 'Date Earned' },
    { key: 'status', header: 'Status' },
    { key: 'actions', header: 'Actions' },
  ];

  const skillTableRows = skills.map(skill => ({
    id: skill.id,
    name: skill.name,
    category: (
      <Tag type="blue" size="sm">
        {skill.category}
      </Tag>
    ),
    level: (
      <Tag
        type={
          skill.level === 'Expert'
            ? 'green'
            : skill.level === 'Advanced'
              ? 'cyan'
              : skill.level === 'Intermediate'
                ? 'yellow'
                : 'gray'
        }
        size="sm"
      >
        {skill.level}
      </Tag>
    ),
    progress: (
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--cds-spacing-03)' }}>
        <ProgressBar
          value={skill.progress}
          max={100}
          label={`${skill.progress}%`}
          hideLabel={false}
          size="sm"
          style={{ flex: 1, minWidth: '100px' }}
        />
      </div>
    ),
    lastUpdated: skill.lastUpdated,
  }));

  const certificationTableRows = certifications.map(cert => ({
    id: cert.id,
    name: cert.name,
    issuer: cert.issuer,
    dateEarned: cert.dateEarned,
    status: (
      <Tag type={cert.status === 'Active' ? 'green' : cert.status === 'Expired' ? 'red' : 'yellow'} size="sm">
        {cert.status}
      </Tag>
    ),
    actions: (
      <Button kind="ghost" size="sm" renderIcon={View} iconDescription="View certificate">
        View
      </Button>
    ),
  }));

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'green';
      case 'In Progress':
        return 'blue';
      default:
        return 'gray';
    }
  };

  if (loading) {
    return (
      <Content>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50vh',
          }}
        >
          <Loading description="Loading professional profile..." />
        </div>
      </Content>
    );
  }

  return (
    <Content>
      <Grid>
        {/* Breadcrumb Navigation */}
        <Column lg={16} md={8} sm={4}>
          <Breadcrumb>
            <BreadcrumbItem href="/dashboard">Dashboard</BreadcrumbItem>
            <BreadcrumbItem href="/profile" isCurrentPage>
              Professional Profile
            </BreadcrumbItem>
          </Breadcrumb>
        </Column>

        {/* Page Header */}
        <Column lg={16} md={8} sm={4} style={{ marginTop: 'var(--cds-spacing-06)' }}>
          <Stack gap={3}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Heading size="lg">Professional Profile</Heading>
              <Button kind="primary" renderIcon={Edit} onClick={() => setIsEditModalOpen(true)}>
                Edit Profile
              </Button>
            </div>
            <p style={{ color: 'var(--cds-text-secondary)' }}>
              Manage your professional information, skills, certifications, and learning goals.
            </p>
          </Stack>
        </Column>

        {/* Error Notification */}
        {error && (
          <Column lg={16} md={8} sm={4}>
            <InlineNotification kind="error" title="Error" subtitle={error} onClose={() => setError(null)} />
          </Column>
        )}

        {/* Professional Information Card */}
        <Column lg={8} md={4} sm={4} style={{ marginTop: 'var(--cds-spacing-06)' }}>
          <Tile style={{ padding: 'var(--cds-spacing-06)' }}>
            <Stack gap={5}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--cds-spacing-04)' }}>
                <User size={32} style={{ color: 'var(--cds-interactive-01)' }} />
                <div>
                  <Heading size="md">{professionalInfo.fullName}</Heading>
                  <p style={{ color: 'var(--cds-text-secondary)', margin: 0 }}>
                    {professionalInfo.title} at {professionalInfo.company}
                  </p>
                </div>
              </div>

              <div>
                <p style={{ margin: '0 0 var(--cds-spacing-03) 0', fontWeight: 500 }}>About</p>
                <p style={{ color: 'var(--cds-text-secondary)', margin: 0 }}>{professionalInfo.bio}</p>
              </div>

              <Grid condensed>
                <Column sm={2}>
                  <p style={{ margin: 0, fontSize: 'var(--cds-type-scale-01)', color: 'var(--cds-text-secondary)' }}>
                    Industry
                  </p>
                  <p style={{ margin: 0, fontWeight: 500 }}>{professionalInfo.industry}</p>
                </Column>
                <Column sm={2}>
                  <p style={{ margin: 0, fontSize: 'var(--cds-type-scale-01)', color: 'var(--cds-text-secondary)' }}>
                    Experience
                  </p>
                  <p style={{ margin: 0, fontWeight: 500 }}>{professionalInfo.yearsExperience} years</p>
                </Column>
              </Grid>
            </Stack>
          </Tile>
        </Column>

        {/* Quick Stats */}
        <Column lg={8} md={4} sm={4} style={{ marginTop: 'var(--cds-spacing-06)' }}>
          <Grid condensed>
            <Column sm={2} md={2} lg={4}>
              <Tile style={{ padding: 'var(--cds-spacing-05)', textAlign: 'center' }}>
                <Certificate
                  size={24}
                  style={{ color: 'var(--cds-support-success)', marginBottom: 'var(--cds-spacing-03)' }}
                />
                <Heading size="sm">{certifications.length}</Heading>
                <p style={{ color: 'var(--cds-text-secondary)', margin: 0, fontSize: 'var(--cds-type-scale-01)' }}>
                  Certifications
                </p>
              </Tile>
            </Column>
            <Column sm={2} md={2} lg={4}>
              <Tile style={{ padding: 'var(--cds-spacing-05)', textAlign: 'center' }}>
                <Growth
                  size={24}
                  style={{ color: 'var(--cds-interactive-01)', marginBottom: 'var(--cds-spacing-03)' }}
                />
                <Heading size="sm">{skills.length}</Heading>
                <p style={{ color: 'var(--cds-text-secondary)', margin: 0, fontSize: 'var(--cds-type-scale-01)' }}>
                  Skills Tracked
                </p>
              </Tile>
            </Column>
            <Column sm={2} md={2} lg={4}>
              <Tile style={{ padding: 'var(--cds-spacing-05)', textAlign: 'center' }}>
                <Trophy
                  size={24}
                  style={{ color: 'var(--cds-support-warning)', marginBottom: 'var(--cds-spacing-03)' }}
                />
                <Heading size="sm">{learningGoals.filter(g => g.status === 'In Progress').length}</Heading>
                <p style={{ color: 'var(--cds-text-secondary)', margin: 0, fontSize: 'var(--cds-type-scale-01)' }}>
                  Active Goals
                </p>
              </Tile>
            </Column>
            <Column sm={2} md={2} lg={4}>
              <Tile style={{ padding: 'var(--cds-spacing-05)', textAlign: 'center' }}>
                <Analytics
                  size={24}
                  style={{ color: 'var(--cds-support-info)', marginBottom: 'var(--cds-spacing-03)' }}
                />
                <Heading size="sm">92%</Heading>
                <p style={{ color: 'var(--cds-text-secondary)', margin: 0, fontSize: 'var(--cds-type-scale-01)' }}>
                  Completion Rate
                </p>
              </Tile>
            </Column>
          </Grid>
        </Column>

        {/* Skills Section */}
        <Column lg={16} md={8} sm={4} style={{ marginTop: 'var(--cds-spacing-07)' }}>
          <Stack gap={5}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Heading size="md">Skills & Competencies</Heading>
              <Button kind="tertiary" size="sm" renderIcon={Add}>
                Add Skill
              </Button>
            </div>

            <DataTable rows={skillTableRows} headers={skillTableHeaders} isSortable size="md">
              {({ rows, headers, getHeaderProps, getTableProps }) => (
                <Table {...getTableProps()}>
                  <TableHead>
                    <TableRow>
                      {headers.map(header => (
                        <TableHeader {...getHeaderProps({ header })}>{header.header}</TableHeader>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map(row => (
                      <TableRow key={row.id}>
                        {row.cells.map(cell => (
                          <TableCell key={cell.id}>{cell.value}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </DataTable>
          </Stack>
        </Column>

        {/* Certifications Section */}
        <Column lg={16} md={8} sm={4} style={{ marginTop: 'var(--cds-spacing-07)' }}>
          <Stack gap={5}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Heading size="md">Certifications & Credentials</Heading>
              <Button kind="tertiary" size="sm" renderIcon={Add}>
                Add Certification
              </Button>
            </div>

            <DataTable rows={certificationTableRows} headers={certificationTableHeaders} isSortable size="md">
              {({ rows, headers, getHeaderProps, getTableProps }) => (
                <Table {...getTableProps()}>
                  <TableHead>
                    <TableRow>
                      {headers.map(header => (
                        <TableHeader {...getHeaderProps({ header })}>{header.header}</TableHeader>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map(row => (
                      <TableRow key={row.id}>
                        {row.cells.map(cell => (
                          <TableCell key={cell.id}>{cell.value}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </DataTable>
          </Stack>
        </Column>

        {/* Learning Goals Section */}
        <Column lg={16} md={8} sm={4} style={{ marginTop: 'var(--cds-spacing-07)' }}>
          <Stack gap={5}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Heading size="md">Learning Goals</Heading>
              <Button kind="tertiary" size="sm" renderIcon={Add}>
                Add Goal
              </Button>
            </div>

            <Grid condensed>
              {learningGoals.map(goal => (
                <Column key={goal.id} lg={8} md={4} sm={4}>
                  <Tile style={{ padding: 'var(--cds-spacing-06)' }}>
                    <Stack gap={4}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Heading size="sm">{goal.title}</Heading>
                        <Tag type={getStatusColor(goal.status)} size="sm">
                          {goal.status}
                        </Tag>
                      </div>

                      <p style={{ color: 'var(--cds-text-secondary)', margin: 0 }}>{goal.description}</p>

                      <div>
                        <p
                          style={{
                            margin: '0 0 var(--cds-spacing-02) 0',
                            fontSize: 'var(--cds-type-scale-01)',
                            color: 'var(--cds-text-secondary)',
                          }}
                        >
                          Progress: {goal.progress}%
                        </p>
                        <ProgressBar value={goal.progress} max={100} label="" hideLabel size="sm" />
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <p
                          style={{
                            margin: 0,
                            fontSize: 'var(--cds-type-scale-01)',
                            color: 'var(--cds-text-secondary)',
                          }}
                        >
                          Target: {goal.targetDate}
                        </p>
                        <Button kind="ghost" size="sm" renderIcon={View}>
                          Details
                        </Button>
                      </div>
                    </Stack>
                  </Tile>
                </Column>
              ))}
            </Grid>
          </Stack>
        </Column>
      </Grid>

      {/* Edit Profile Modal */}
      <Modal
        open={isEditModalOpen}
        modalHeading="Edit Professional Profile"
        primaryButtonText="Save Changes"
        secondaryButtonText="Cancel"
        onRequestClose={() => setIsEditModalOpen(false)}
        onRequestSubmit={() => {
          // Handle save logic here
          setIsEditModalOpen(false);
        }}
      >
        <Form>
          <Stack gap={5}>
            <FormGroup legendText="Basic Information">
              <Stack gap={4}>
                <TextInput
                  id="fullName"
                  labelText="Full Name"
                  value={professionalInfo.fullName}
                  onChange={e =>
                    setProfessionalInfo({
                      ...professionalInfo,
                      fullName: e.target.value,
                    })
                  }
                />
                <TextInput
                  id="title"
                  labelText="Job Title"
                  value={professionalInfo.title}
                  onChange={e =>
                    setProfessionalInfo({
                      ...professionalInfo,
                      title: e.target.value,
                    })
                  }
                />
                <TextInput
                  id="company"
                  labelText="Company"
                  value={professionalInfo.company}
                  onChange={e =>
                    setProfessionalInfo({
                      ...professionalInfo,
                      company: e.target.value,
                    })
                  }
                />
                <TextArea
                  id="bio"
                  labelText="Professional Bio"
                  value={professionalInfo.bio}
                  onChange={e =>
                    setProfessionalInfo({
                      ...professionalInfo,
                      bio: e.target.value,
                    })
                  }
                  rows={4}
                />
              </Stack>
            </FormGroup>
          </Stack>
        </Form>
      </Modal>
    </Content>
  );
};

export default ProfessionalProfile;
